import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';

const DeliveryAlternativesList = ({
  alternatives,
  onSelectDelivery,
  colSpan,
  setExpandedRows,
  currentDelivery,
  paymentMethod,
  isLoading,
  parentTableStructure = false, // Новый пропс для определения структуры таблицы
  isOriginal = false // Для определения количества колонок в родительской таблице
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const isValidAlternative = (alternative) => {
    if (!alternative) {
      return false;
    }

    const trips = Number(alternative.numberOfTrips);
    const tripCost = Number(alternative.costPerTrip);
    const totalCost = Number(alternative.totalCost);

    return Number.isFinite(trips) && Number.isFinite(tripCost) && Number.isFinite(totalCost);
  };

  const handleSelect = (alternative) => {
    onSelectDelivery(alternative);
    setExpandedRows(false);
  };

  // Создаем объект для текущей доставки в формате альтернативы
  const currentDeliveryAsAlternative = React.useMemo(() => {
    if (!currentDelivery) return null;
    
    return {
      name: currentDelivery.name,
      numberOfTrips: currentDelivery.numberOfTrips,
      costPerTrip: currentDelivery.costPerTrip,
      totalCost: currentDelivery.totalCost,
      usedCentralized: currentDelivery.usedCentralized,
      isCurrentDelivery: true, // Флаг для выделения
    };
  }, [currentDelivery]);

  const filteredAlternatives = React.useMemo(() => {
    let filtered = alternatives;
    
    // Убираем дубли - исключаем из альтернатив те, что совпадают с текущей доставкой по названию
    if (currentDelivery) {
      filtered = alternatives.filter((alt) => alt.name !== currentDelivery.name);
    }

    filtered = filtered.filter((alt) => isValidAlternative(alt));
    
    // Фильтруем по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(alt => 
        alt.name.toLowerCase().includes(query)
      );
    }
    
    // Сортируем от самого дешевого к самому дорогому
    filtered = filtered.sort((a, b) => a.totalCost - b.totalCost);
    
    // Добавляем текущую доставку в начало списка если есть
    if (currentDeliveryAsAlternative) {
      // Проверяем, подходит ли текущая доставка под поисковый запрос
      const currentMatches = !searchQuery.trim() || 
        currentDeliveryAsAlternative.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (currentMatches) {
        return [currentDeliveryAsAlternative, ...filtered];
      }
    }
    
    return filtered;
  }, [alternatives, searchQuery, currentDeliveryAsAlternative, currentDelivery]);

  const formatPrice = (price) => {
    const normalizedPrice = Number(price);
    if (!Number.isFinite(normalizedPrice)) {
      return '—';
    }

    return new Intl.NumberFormat('ru-RU').format(Math.ceil(normalizedPrice));
  };

  if (isLoading) {
    return (
      <tr className="rd-kp-alts-row">
        <td colSpan={colSpan} className="rd-kp-alts-cell">
          <div className="rd-kp-alts">
            <div className="rd-kp-alts__empty">
              <CircularProgress size={20} />
              <div style={{ marginTop: '8px' }}>Загрузка альтернативных способов доставки...</div>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="rd-kp-alts-row">
      <td colSpan={colSpan} className="rd-kp-alts-cell">
        <div className="rd-kp-alts rd-kp-alts--delivery">
          {/* Поиск */}
          <div className="rd-kp-alts__search">
            <input
              type="text"
              placeholder="Поиск по способу доставки"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {filteredAlternatives.length > 0 ? (
            filteredAlternatives.map((alternative, index) => {
              const isCurrentDelivery = alternative.isCurrentDelivery;
              const savings = currentDelivery && !isCurrentDelivery
                ? currentDelivery.totalCost - alternative.totalCost
                : 0;

              const rowClass = 'rd-kp-alts__row' + (isCurrentDelivery ? ' rd-kp-alts__row--current' : '');

              return (
                <div className={rowClass} key={index}>
                  {/* Название */}
                  <div className="rd-kp-alts__title">
                    {alternative.name}
                    {alternative.usedCentralized && <small>Централизованная доставка</small>}
                  </div>

                  {/* Кол-во рейсов */}
                  <span className="rd-kp-alts__trips">{alternative.numberOfTrips}</span>

                  {/* Ед. */}
                  <span className="rd-kp-alts__unit">рейс</span>

                  {/* Сумма + экономия/удорожание */}
                  <span className="rd-kp-alts__price">
                    {formatPrice(alternative.totalCost)} ₽
                    {savings !== 0 && (
                      <small className={savings > 0 ? '' : 'is-negative'}>
                        {savings > 0 ? '−' : '+'}{formatPrice(Math.abs(savings))} ₽
                      </small>
                    )}
                  </span>

                  {/* Действие */}
                  {isCurrentDelivery ? (
                    <button className="rd-kp-alts__choose" type="button">текущий</button>
                  ) : (
                    <button
                      className="rd-kp-alts__choose"
                      type="button"
                      onClick={() => handleSelect(alternative)}
                    >
                      выбрать
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="rd-kp-alts__empty">
              {searchQuery ? 'Ничего не найдено по запросу' : 'Нет альтернативных способов доставки'}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default DeliveryAlternativesList;
